import { useMemo, useState } from "react";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useBoard } from "@/hooks/useBoard";
import peopleData from "@/data/peopleBoard.json";
import { PeopleClient } from "./PeopleClient";
import { Card } from "@/design/primitives";
import { AuthGate } from "@/screens/cc";


const BRAND_SCOPE_MAP = {
  tjb: { slug: "truejoybirthing", name: "True Joy Birthing" },
  at: { slug: "agentictrust", name: "AgenticTrust" },
  to: { slug: "trustoffice", name: "TrustOffice" },
  av: { slug: "aeriusview", name: "AeriusView" },
  wp: { slug: "wingpoint", name: "WingPoint" },
  tm: { slug: "trustminutes", name: "TrustMinutes" },
  sv: { slug: "socializevideo", name: "SocializeVideo" },
  sd: { slug: "stenodesk", name: "StenoDesk" },
};

export default function PeopleBoardPage({ brandScope }) {
  const desktop = useMediaQuery("(min-width: 1024px)");
  const board = useBoard("people");
  const [dismissed, setDismissed] = useState(false);

  const scopeInfo = brandScope ? BRAND_SCOPE_MAP[brandScope] : null;

  const liveItems = useMemo(() => (board.items || []).map((i) => {
    const p = i.payload || {};
    if (i.kind === "person" && p.name && !i.name) {
      return { ...p, ...i };
    }
    return i;
  }), [board.items]);

  const bakedItems = useMemo(() =>
    (peopleData.cards || []).map((c) => ({ ...c, payload: c.payload || {} })),
    [],
  );
  const viewItemsRaw = board.live && liveItems.length ? liveItems : bakedItems;
  const viewMeta = board.live && board.meta ? board.meta : peopleData.stamp;

  // Scope items when brandScope is set. Segment rows (people-segment) are structural:
  // keep the one matching this brand (slug 'brand:<Name>') so the scoped rail + default
  // segment still render; person rows filter on the brands array (display names).
  const viewItems = useMemo(() => {
    if (!scopeInfo) return viewItemsRaw;
    return viewItemsRaw.filter((i) => {
      if (i.kind === "people-segment") return i.slug === `brand:${scopeInfo.name}`;
      const brands = i.payload?.brands || [];
      return brands.includes(scopeInfo.name);
    });
  }, [viewItemsRaw, scopeInfo]);

  if (board.status === "needs-signin") {
    return (
      <div data-testid="people-page" className="w-full font-inter text-os-ink">
        <div className={desktop ? "mx-auto flex max-w-[100rem] flex-col gap-4 px-10 py-6" : "flex flex-col gap-3 px-4 pb-8 pt-4"}>
          <AuthGate />
          <PeopleClient items={scopeInfo ? viewItems : bakedItems} meta={peopleData.stamp} board={board} variant={desktop ? "desktop" : "mobile"} onNotice={() => {}} brandScope={scopeInfo} />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="people-page" className="w-full font-inter text-os-ink">
      <div className={desktop ? "mx-auto flex max-w-[100rem] flex-col gap-4 px-10 py-6" : "flex flex-col gap-3 px-4 pb-8 pt-4"}>
        {!board.live && (
          <Card quiet className="px-5 py-3">
            <p className="text-[0.875rem] leading-[1.45] text-os-ink-2">
              Kit's people feed is offline this visit — the page shows the last committed window.
            </p>
          </Card>
        )}
        {board.live && !dismissed && !board.meta?.ingested_at && (
          <button
            type="button"
            data-testid="people-sample-note"
            onClick={() => setDismissed(true)}
            className="self-start rounded-control border border-os-border px-3 py-1.5 text-left font-mono text-[0.6875rem] text-os-ink-3 hover:text-os-ink"
          >
            Today this window is a hand-built sample — Kit's live people sync arrives on the first fetch. Tap to dismiss.
          </button>
        )}
        <PeopleClient items={viewItems} meta={viewMeta} board={board} variant={desktop ? "desktop" : "mobile"} onNotice={board.setNotice} brandScope={scopeInfo} />
        {board.notice?.text && (
          <p className="px-1 text-[0.8125rem] text-os-amber" data-testid="people-notice">{board.notice.text}</p>
        )}
      </div>
    </div>
  );
}
